import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { Order } from '@/mocks/orders';
import { orders as initialOrders } from '@/mocks/orders';
import KanbanColumn from './KanbanColumn';
import KanbanCard from './KanbanCard';

interface Column {
  id: string;
  title: string;
  color: string;
  statuses: string[];
}

const columns: Column[] = [
  { id: 'col-yeni', title: 'Yeni Sipariş', color: 'bg-status-blue', statuses: ['yeni'] },
  { id: 'col-cizimde', title: 'Çizim Aşaması', color: 'bg-status-purple', statuses: ['cizimde'] },
  { id: 'col-cizim_onay', title: 'Çizim Onayı Bekliyor', color: 'bg-status-purple', statuses: ['cizim_onay'] },
  { id: 'col-kesimde', title: 'Kesim Aşaması', color: 'bg-status-orange', statuses: ['kesimde'] },
  { id: 'col-aydinlatma', title: 'Aydınlatma / Montaj', color: 'bg-status-yellow', statuses: ['aydinlatma'] },
  { id: 'col-test', title: 'Test Aşaması', color: 'bg-status-yellow', statuses: ['test'] },
  { id: 'col-paketleme', title: 'Paketleme', color: 'bg-status-navy', statuses: ['paketleme'] },
  { id: 'col-hazir', title: 'Kargoya Hazır', color: 'bg-status-navy', statuses: ['kargoya_hazir'] },
];

const statusToColumnMap: Record<string, string> = {};
columns.forEach(col => {
  col.statuses.forEach(status => {
    statusToColumnMap[status] = col.id;
  });
});

export default function KanbanBoard() {
  const [ordersList, setOrdersList] = useState<Order[]>(() =>
    initialOrders.filter(o => o.status !== 'teslim' && o.status !== 'iptal' && o.status !== 'sorunlu' && o.status !== 'kargoda')
  );
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const getColumnOrders = (columnId: string) => {
    return ordersList.filter(order => statusToColumnMap[order.status] === columnId);
  };

  const findColumnOfOrder = (orderId: string): string | null => {
    const order = ordersList.find(o => o.id === orderId);
    if (!order) return null;
    return statusToColumnMap[order.status] || null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const order = ordersList.find(o => o.id === event.active.id);
    if (order) setActiveOrder(order);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveOrder(null);
    const { active, over } = event;
    if (!over) return;

    const activeOrderId = active.id as string;
    const order = ordersList.find(o => o.id === activeOrderId);
    if (!order) return;

    // Determine target column
    let targetColumnId: string | null = null;

    // Check if dropped on a column directly
    const isColumn = columns.some(col => col.id === over.id);
    if (isColumn) {
      targetColumnId = over.id as string;
    } else {
      // Dropped on another card - find its column
      targetColumnId = findColumnOfOrder(over.id as string);
    }

    if (!targetColumnId) return;

    // Find target status
    const targetColumn = columns.find(col => col.id === targetColumnId);
    if (!targetColumn) return;

    const newStatus = targetColumn.statuses[0]; // Use first status in the column

    // Don't update if same column
    if (statusToColumnMap[order.status] === targetColumnId) return;

    // Update order status
    setOrdersList(prev =>
      prev.map(o => o.id === activeOrderId ? { ...o, status: newStatus as Order['status'] } : o)
    );
  };

  const totalOrders = ordersList.length;

  return (
    <div>
      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <span className="text-stone-500">Toplam aktif iş: <strong className="text-stone-800">{totalOrders}</strong></span>
        <span className="text-stone-300">|</span>
        <span className="text-xs text-stone-400">
          Kartları sürükleyerek aşamalar arasında taşıyabilirsiniz
        </span>
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-3 overflow-x-auto kanban-scroll pb-4">
          {columns.map((col) => {
            const colOrders = getColumnOrders(col.id);
            return (
              <KanbanColumn
                key={col.id}
                id={col.id}
                title={col.title}
                color={col.color}
                orders={colOrders}
                count={colOrders.length}
              />
            );
          })}
        </div>

        <DragOverlay>
          {activeOrder ? (
            <div className="rotate-2 opacity-90 w-[280px]">
              <KanbanCard order={activeOrder} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}